import { switchMap, map, filter, takeUntil, take, find } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { Firestore, collection, doc, getDocsFromServer, orderBy, query, where, writeBatch } from '@angular/fire/firestore';
import { Website, WebsitePage, WebsitePageType } from '@models';
import { BaseService, ToastService } from '@services';
import { Router } from '@angular/router';
import { EMPTY, from, Observable, of } from 'rxjs';
import { getWebsiteUrl } from 'src/app/core/helpers/url-routing';

import { TeamService } from '../team.service';

import { WebsitePageTypeService } from './website-page-type.service';

@Injectable({
  providedIn: 'root',
})
export class WebsiteService extends BaseService<Website> {
  protected readonly documentPath = 'websites';
  private websites: Website[];

  constructor(
    protected db: Firestore,
    protected toastService: ToastService,
    protected websitepageTypeService: WebsitePageTypeService,
    protected teamService: TeamService,
    private router: Router
  ) {
    super(db, toastService);
  }

  /**
   * This function gets the website from the current subdomain. It uses a cached list of subdomains which is retrieved at the first request.
   * Returns null when the subdomain is not found.
   *
   * @returns Observable<Website | null>
   */
  getWebsiteFromSubdomain(): Observable<Website | null> {
    this.logService.log('[getWebsiteFromSubdomain]');
    const urlFragments = location.hostname.split('.');
    if (urlFragments.length > 2) {
      const subdomain = urlFragments[0];
      this.logService.info('[getWebsiteFromSubdomain] subdomain:', subdomain);
      return this.getAllCached().pipe(
        switchMap((websites) => {
          this.websites = websites;
          let fIndex = websites.findIndex((s) => s.subdomain === subdomain);
          if (fIndex > -1) {
            let website = websites[fIndex];
            this.logService.info('[getWebsiteFromSubdomain] website found:', website.title);

            // check if website needs to be redirected
            if (website.redirectTo) {
              this.logService.info('[getWebsiteFromSubdomain] needs to be redirected:', website.redirectTo);
              fIndex = websites.findIndex((s) => s.subdomain === website.redirectTo);
              website = websites[fIndex];
              this.logService.info('[getWebsiteFromSubdomain] redirecting to website:', website.title);
              window.location.href = getWebsiteUrl(website.subdomain);
              return EMPTY;
            }

            // check if website is enabled
            if (website.active) {
              return of(website);
            } else {
              return of(null);
            }
          } else {
            return this.createDefaultWebsite(subdomain);
          }
        }),
        switchMap((website) => {
          if (!website) {
            return of(null);
          }
          if (website.redirectTo) {
            window.location.href = website.redirectTo;
            return of(null);
          } else {
            return of(website);
          }
        })
      );
    } else {
      return of(null);
    }
  }

  createDefaultWebsite(subdomain: string): Observable<Website> {
    this.logService.info('[createDefaultWebsite] subdomain:', subdomain);
    return this.teamService.getTeamFromSubdomain(subdomain).pipe(
      map((teams) => {
        if (teams.length > 0) {
          this.logService.info('[createDefaultWebsite] team:', teams);
          const team = teams[0];
          const website = {
            teamId: team.globalId,
            title: team.teamnaam,
            active: true,
            alternateContactInfo: false,
            showContactInfo: true,
            subdomain: subdomain,
            redirectTo: null,
          } as Website;

          return website;
        } else {
          return null;
        }
      })
    );
  }

  getByTeamId(teamId: string) {
    return this.getWithQuery([where('teamId', '==', teamId)]).pipe(map((websites) => (websites.length > 0 ? websites[0] : null)));
  }

  getDefaultPageTypes(): Observable<WebsitePageType[]> {
    const pageTypes = query(collection(this.db, 'websitePageTypes'), where('defaultPage', '==', true));
    return from(getDocsFromServer(pageTypes)).pipe(
      map((snapshot) => snapshot.docs.map((item) => this.convertFirestoreTimeStamps(item.data() as WebsitePageType)))
    );
  }

  async createWithPages(website: Website, pages: WebsitePage[]): Promise<void> {
    if (pages.length > 499) {
      throw new Error('Een website kan maximaal 499 standaardpagina’s bevatten.');
    }
    const batch = writeBatch(this.db);
    batch.set(doc(this.db, `${this.documentPath}/${website.documentId}`), website);
    for (const page of pages) {
      batch.set(doc(this.db, `${this.documentPath}/${website.documentId}/pages/${page.documentId}`), page);
    }
    await batch.commit();
  }

  getWebsitePages(websiteId: string) {
    return this.getFromSubCollectionWithQuery<WebsitePage>(websiteId, 'pages', [orderBy('order', 'asc')]);
  }

  getWebsitePageById(websiteId: string, websitePageId: string) {
    return this.getByIdFromSubCollection<WebsitePage>(websiteId, 'pages', websitePageId);
  }

  addPage(websiteId: string, page: WebsitePage) {
    return this.addToSubcollection(websiteId, 'pages', page);
  }

  updatePage(websiteId: string, page: WebsitePage) {
    return this.updateSubCollectionDoc(websiteId, 'pages', page);
  }

  deletePage(websiteId: string, pageId: string) {
    return this.removeFromSubcollection(websiteId, 'pages', pageId);
  }
}
